<template>
  <div class="panel-container">
    <!-- 头部容器 -->
    <div class="header-container">
      <!-- 标题 -->
      <div class="title-container">
        <div class="symbol-icon"></div>
        <span class="title-text">{{ $str.getKey('收益管理/充放电总览') }}</span>
      </div>
      <!-- 操作按钮 -->
      <div class="btns-container">
      </div>
    </div>
    <div class="divider-line"></div>
    <!-- 主体容器 -->
    <div class="main-container">
      <template v-for="item in panelData.infoList" :key="item">
        <div class="info-item-container">
          <div class="right-info-wrapper">
            <div>
              <span class="title-text">{{item.title}}</span>
              <el-tooltip :content="item.tip" placement="top" :hide-after="0" v-if="item.tip">
                <img :src="alertCircleIcon" alt="" class="tip-icon tip-icon-svg" />
              </el-tooltip>
            </div>
            <div>
              <span class="value-text">{{item.value}}</span>
              <span class="unit-text">{{item.unit}}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onBeforeUnmount } from "vue"
import dayjs from 'dayjs';
import { ws_req } from "@/ws/wss-request";
import useAppStore from '@/store/modules/app'
import alertCircleIcon from '@/assets/icons/svg/alert-circle.svg'

const $str = window.$str;

/** 获取stationId，并监听其变化-更新数据 */
const appStore = useAppStore();
const stationId = computed(() => appStore.stationId);
const isOldStation = computed(() => appStore.isOldStation);

// 监听 stationId 变化
watch(() => appStore.stationId, () => {
  refreshInfoList();
}, { immediate: true });


/** 设置组件配置项 */
defineOptions({
  name: "chartPanel"
});


const panelData = reactive({
  infoList: [
    {
      title: $str.getKey('收益管理/累计收益'),
      key: 'incomeSum',
      value: '0',
      unit: $str.getKey('收益管理/万元'),
      icon: new URL('./assets/icon_ljsy.svg', import.meta.url).href,
      tip: $str.getKey('收益管理/日收益之和'),
      formatValueFn: (value) => isNaN(value) ? value : (value / 10000).toFixed(2)
    },
    {
      key: 'efficiency',
      title: $str.getKey('收益管理/系统效率'),
      value: '--',
      unit: '%',
      icon: new URL('./assets/icon_xtxl.svg', import.meta.url).href,
      tip: $str.getKey('收益管理/系统效率 = 累计放电量 / 累计充电量'),
      formatValueFn: (value) => isNaN(value) ? value : (value * 100).toFixed(2)
    },
    {
      title: $str.getKey('收益管理/今日放电'),
      key: 'reversePowerToday',
      value: '0',
      unit: 'kWh',
      icon: alertCircleIcon,
      tip: isOldStation.value ? $str.getKey('收益管理/今日放电量 = (关口电表反向有功电能今日值)/10') : $str.getKey('收益管理/今日放电量 = 关口电表反向有功电能今日值'),
      formatXwhInfoFn: formatValueAndUnitForXwh,
    },
    {
      title: $str.getKey('收益管理/今日充电'),
      key: 'positivePowerToday',
      value: '0',
      unit: 'kWh',
      icon: new URL('./assets/icon_jrcd.svg', import.meta.url).href,
      tip: isOldStation.value ? $str.getKey('收益管理/今日充电量 = (关口电表正向有功电能今日值)/10') : $str.getKey('收益管理/今日充电量 = 关口电表正向有功电能今日值'),
      formatXwhInfoFn: formatValueAndUnitForXwh,
    },
    {
      title: $str.getKey('收益管理/累计放电'),
      key: 'reversePowerSum',
      value: '0',
      unit: 'kWh',
      icon: new URL('./assets/icon_ljfd.svg', import.meta.url).href,
      tip: $str.getKey('收益管理/累计放电=投运日至当前时间放电量之和'),
      formatXwhInfoFn: formatValueAndUnitForXwh,
    },
    {
      title: $str.getKey('收益管理/累计充电'),
      key: 'positivePowerSum',
      value: '0',
      unit: 'kWh',
      icon: new URL('./assets/icon_ljcd.svg', import.meta.url).href,
      tip: $str.getKey('收益管理/累计充电= 投运日至当前时间充电量之和'),
      formatXwhInfoFn: formatValueAndUnitForXwh,
    },
  ],
  wsTimer: null,
})




initPanel();

onBeforeUnmount(() => {
  if (panelData.wsTimer) {
    clearInterval(panelData.wsTimer);
    panelData.wsTimer = null;
  }
})

/** ====== [业务：功能方法] =================================================================== */
/** 初始化面板 */
function initPanel() {
  panelData.wsTimer = setInterval(() => {
    refreshInfoList();
  }, 5000);

}



/** ====== [辅助：功能方法] =================================================================== */

function refreshInfoList() {
  const params = {
    stationId: stationId.value
  }
  ws_req.request('ws_main_monitoring', params).then(res => {
    panelData.infoList.forEach(it => {
      if (it.key === 'efficiency') {
        if (isNaN(res['reversePowerSum']) || isNaN(res['positivePowerSum'])) {
          it.value = '--';
        } else {
          const efficiency = res['reversePowerSum'] / res['positivePowerSum'];
          if (efficiency < 0.85 || efficiency > 0.95) {
            it.value = '--';
          } else {
            it.value = efficiency.toFixed(4);
          }
          if (typeof(it.formatValueFn) === 'function') {
            it.value = it.formatValueFn(it.value);
          }
        }

      } else {
        it.value = res[it.key] ?? '--';
        if (typeof(it.formatValueFn) === 'function') {
          it.value = it.formatValueFn(it.value);
        }
        if (typeof(it.formatXwhInfoFn) === 'function') {
          const {value, unit} = it.formatXwhInfoFn(it.value);
          it.value = value;
          it.unit = unit;
        }
      }
    })


  })
}


/** 格式化数字：最多保留bits位小数 >>> 四舍五入 */
function formatNumber(num, bits = 4) {
    // 将数字转换为字符串
    const numStr = num.toString();
    
    // 检查小数点的位置
    const decimalIndex = numStr.indexOf('.');
    
    // 如果没有小数部分，直接返回原数
    if (decimalIndex === -1) {
        return numStr; // 返回整数部分
    }
    
    // 获取小数部分
    const decimalPart = numStr.slice(decimalIndex + 1);
    
    // 如果小数位数小于或等于 4，返回原数
    if (decimalPart.length <= bits) {
        return numStr; // 返回原数
    }
    
    // 四舍五入保留 4 位小数
    return Number(num).toFixed(bits); // 使用 toFixed(4) 进行四舍五入
}


/**
 * 格式化电量数值和单位
 */
function formatValueAndUnitForXwh(value) {
  const res = {
    value: value,
    unit: 'kWh',
  }

  if (isNaN(value)) {
    return res;
  } else {
    res.value = +value;
  }

  const gWh = 1*1000*1000;
  if (res.value > gWh) {
    res.value = (res.value / gWh).toFixed(2);
    res.unit = 'GWh';
    return res;
  }

  const mWh = 1*1000;
  if (res.value > mWh) {
    res.value = (res.value / mWh).toFixed(2);
    res.unit = 'MWh';
    return res;
  }

  res.value = res.value.toFixed(2);
  return res;
}



</script>
<style lang="scss" scoped>
.panel-container {
  --page-top: 12px;
  --header-hight: 50px;
  & {
    width: 100%;
    height: 100%;
    // 外层 overview-block 已提供白色卡片和阴影，这里只负责内容布局
    background: transparent;
    border-radius: 0;
    border: none;
    padding: 4px 0 0 0;
    overflow: hidden;
  
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .header-container {
    width: 100%;
    height: var(--header-hight);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0px 10px;

    .title-container {
      height: 100%;
      flex: 1;
      padding-left: 1px;
      display: flex;
      align-items: center;
      gap: 10px;

      .symbol-icon {
        display: none;
      }
      .title-text {
        color: #2E2E2E;
        font-family: "HarmonyOS Sans SC";
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }
    }

    .btns-container {
      max-width: 50%;
      height: 100%;
      padding-right: 12px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      // gap: 10px;
    }
  }

  .divider-line {
    width: 100%;
    height: 1px;
    background: rgba(192, 207, 218, 0.5);
  }

  .main-container {
    & {
      width: 100%;
      height: calc(100% - var(--header-hight));
      flex: 1;
      overflow: hidden;
      padding: 8px 4px 4px 12px;

      /* 一排 2 个指标，3 行网格布局 */
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      row-gap: 14px;
      column-gap: 12px;
    }

    .info-item-container {
      width: 100%;

      display: flex;
      gap: 12px;

      .right-info-wrapper {
        flex: 1;
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .title-text {
          color: rgba(46, 46, 46, 0.60);
          font-family: "HarmonyOS Sans SC";
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }
        .tip-icon {
          cursor: pointer;
          font-size: 14px;
          color: rgba(46, 46, 46, 0.6);
          margin-left: 4px;
        }
        .tip-icon-svg {
          width: 14px;
          height: 14px;
          display: inline-block;
          vertical-align: middle;
        }
        .value-text {
          color: #2E2E2E;
          font-family: "HarmonyOS Sans SC";
          font-size: 20px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        }
        .unit-text {
          color: #2E2E2E;
          font-family: "HarmonyOS Sans SC";
          font-size: 12px;
          font-style: normal;
          font-weight: 250;
          line-height: normal;
        }
      }
    }



  }

}


</style>
